import { useState } from "react";
import { ArrowUpRight, Mail, Phone, MapPin, Send } from "lucide-react";

export default function DigtekContact() {
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "About Price",
        message: "",
    });

    const onSubmit = (e) => {
        e.preventDefault();
        // Optimistic UI; a real backend endpoint can be wired in later
        setSubmitted(true);
    };

    return (
        <section
            id="contact"
            data-testid="digtek-contact"
            className="relative py-24 lg:py-32 bg-white overflow-hidden"
        >
            <div className="digtek-blob lime w-[340px] h-[340px] -right-24 top-24 opacity-40" />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
                <div className="text-center mb-14">
                    <div className="digtek-eyebrow mb-4 justify-center">
                        <span>Contact us</span>
                    </div>
                    <h2
                        data-testid="contact-heading"
                        className="font-display font-bold text-[40px] sm:text-[52px] lg:text-[62px] leading-[1.02] text-digtek-header max-w-[820px] mx-auto"
                    >
                        How can we help you{" "}
                        <em className="not-italic text-digtek-purple">
                            grow today?
                        </em>
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Form */}
                    <div className="lg:col-span-7 bg-digtek-bg rounded-3xl p-8 lg:p-12">
                        <h3 className="font-display font-bold text-3xl text-digtek-header mb-6">
                            Get in touch
                        </h3>
                        {submitted ? (
                            <div
                                data-testid="contact-success"
                                className="bg-white rounded-2xl p-8 border-2 border-digtek-lime text-center"
                            >
                                <div className="w-16 h-16 rounded-full bg-digtek-lime flex items-center justify-center mx-auto mb-4">
                                    <Send className="w-7 h-7 text-digtek-header" />
                                </div>
                                <h4 className="font-display font-bold text-2xl text-digtek-header mb-2">
                                    Thanks — we've got your note.
                                </h4>
                                <p className="text-digtek-text">
                                    A partner will reply within 24 business hours.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                className="space-y-5"
                                data-testid="contact-form"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <input
                                        required
                                        data-testid="contact-name"
                                        placeholder="Your name"
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                name: e.target.value,
                                            })
                                        }
                                        className="w-full h-14 px-5 rounded-full bg-white border border-digtek-border focus:border-digtek-purple focus:outline-none text-digtek-header placeholder:text-digtek-text/60"
                                    />
                                    <input
                                        required
                                        type="email"
                                        data-testid="contact-email"
                                        placeholder="Email address"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                email: e.target.value,
                                            })
                                        }
                                        className="w-full h-14 px-5 rounded-full bg-white border border-digtek-border focus:border-digtek-purple focus:outline-none text-digtek-header placeholder:text-digtek-text/60"
                                    />
                                </div>
                                <select
                                    data-testid="contact-subject"
                                    value={form.subject}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            subject: e.target.value,
                                        })
                                    }
                                    className="w-full h-14 px-5 rounded-full bg-white border border-digtek-border focus:border-digtek-purple focus:outline-none text-digtek-header"
                                >
                                    <option>About Price</option>
                                    <option>About a project</option>
                                    <option>Partnership</option>
                                    <option>Careers</option>
                                    <option>Something else</option>
                                </select>
                                <textarea
                                    required
                                    data-testid="contact-message"
                                    placeholder="Tell us about the shape of the project..."
                                    rows={5}
                                    value={form.message}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            message: e.target.value,
                                        })
                                    }
                                    className="w-full p-5 rounded-3xl bg-white border border-digtek-border focus:border-digtek-purple focus:outline-none text-digtek-header placeholder:text-digtek-text/60 resize-none"
                                />
                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    className="btn-digtek"
                                >
                                    Submit now
                                    <ArrowUpRight className="w-4 h-4" />
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Info */}
                    <div className="lg:col-span-5">
                        <h3 className="font-display font-bold text-3xl text-digtek-header mb-8">
                            Contact info
                        </h3>
                        <div className="space-y-6">
                            {[
                                {
                                    icon: MapPin,
                                    label: "Studio address",
                                    value: "Asia-Pacific · Remote-first",
                                },
                                {
                                    icon: Phone,
                                    label: "Call us",
                                    value: "+1 (646) 444-7966",
                                    href: "tel:+16464447966",
                                },
                                {
                                    icon: Mail,
                                    label: "Email",
                                    value: "info@aximo.studio",
                                    href: "mailto:info@aximo.studio",
                                },
                            ].map((c) => {
                                const Icon = c.icon;
                                const body = (
                                    <div className="flex items-start gap-4 group">
                                        <span className="w-14 h-14 rounded-full bg-digtek-bg group-hover:bg-digtek-purple text-digtek-purple group-hover:text-digtek-lime flex items-center justify-center shrink-0 transition-colors">
                                            <Icon className="w-6 h-6" />
                                        </span>
                                        <div>
                                            <p className="text-sm text-digtek-text font-semibold uppercase tracking-wider mb-1">
                                                {c.label}
                                            </p>
                                            <p className="text-lg font-display font-bold text-digtek-header group-hover:text-digtek-purple transition-colors">
                                                {c.value}
                                            </p>
                                        </div>
                                    </div>
                                );
                                return c.href ? (
                                    <a
                                        key={c.label}
                                        href={c.href}
                                        data-testid={`contact-info-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                                    >
                                        {body}
                                    </a>
                                ) : (
                                    <div key={c.label}>{body}</div>
                                );
                            })}
                        </div>

                        {/* Newsletter CTA */}
                        <div className="mt-10 relative rounded-3xl overflow-hidden bg-digtek-header p-8">
                            <div className="digtek-blob lime w-[220px] h-[220px] -right-12 -bottom-12 opacity-30" />
                            <p className="text-digtek-lime uppercase tracking-wider text-xs font-bold mb-2">
                                Newsletter
                            </p>
                            <h4 className="font-display font-bold text-2xl text-white leading-tight mb-4">
                                Growth tactics every Friday.
                            </h4>
                            <p className="text-white/70 text-sm mb-4">
                                One idea, one experiment, one screenshot. 12k
                                founders read it.
                            </p>
                            <div className="relative flex items-center bg-white rounded-full p-1.5">
                                <input
                                    placeholder="your@email.com"
                                    className="flex-1 h-11 pl-4 pr-2 bg-transparent focus:outline-none text-digtek-header text-sm"
                                />
                                <button className="btn-digtek-lime h-11 px-5 text-[11px]">
                                    Subscribe
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
